/**
 * The sign-in sheet, for a membership page that needs a session before it can act (GH-864e):
 * "Join on the web" on an invite, "Join the waitlist", and the event's auto-invite.
 *
 * The same door as everywhere else — `SignInChoices` with the GH-865 Apple and Google row, then the
 * phone and its code — and the same mechanisms behind it (`useProviderSignIn`,
 * `completePhoneSignIn`), so a donor remembered here is remembered on every page and nothing about
 * signing in is invented twice. What happens *after* is the host's: `onIssued` runs once the device
 * is remembered, and the host closes the sheet.
 */
import { useRef, useState } from "react";
import type { DonorSessionResponse } from "@givehere/contracts";
import { useDialogFocus } from "@givehere/ui";
import {
  completePhoneSignIn,
  DonorApiError,
  startDonorSignIn,
  storedToken,
} from "./donor-session.js";
import { InviteCodeEntry, InviteRequiredStep, useSignUpAccess } from "./InviteAccess.js";
import { useProviderSignIn } from "./provider-sign-in.js";
import { isSignUpRefusal, type DoorAttempt } from "./sign-up-access.js";
import { CodeStep, SignInChoices } from "./SignInSteps.js";

type Step =
  | { kind: "choices" }
  | { kind: "code"; phone: string; verificationId: string; hint: string | null }
  /** GH-871b: a new person without a usable invite — the code field and the waitlist. */
  | { kind: "invite"; attempt: DoorAttempt; refusal: DonorApiError };

export function SignInSheet({
  heading,
  lede,
  onIssued,
  onDismiss,
  inviteCode = null,
  eventJoinToken = null,
}: {
  heading: string;
  lede: string;
  onIssued: (issued: DonorSessionResponse) => Promise<void> | void;
  onDismiss: () => void;
  /** GH-871b: the code this sheet was opened for (`/i/<code>`), prefilled and sent on every door. */
  inviteCode?: string | null;
  /** GH-871b: the big screen's join token (`?join=`), sent on every door. */
  eventJoinToken?: string | null;
}) {
  const panelRef = useDialogFocus<HTMLDivElement>();
  const [step, setStep] = useState<Step>({ kind: "choices" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** One confirm per code (GH-856): a second press would send a spent code. */
  const confirmed = useRef(false);
  const access = useSignUpAccess({ prefillCode: inviteCode, joinToken: eventJoinToken });
  const refused = (attempt: DoorAttempt, refusal: DonorApiError) => {
    setError(null);
    setStep({ kind: "invite", attempt, refusal });
  };
  const providers = useProviderSignIn({
    enabled: true,
    setBusy,
    setError,
    signUpAccess: access.fields,
    onSignUpRefused: refused,
  });

  const fail = (caught: unknown, fallback: string) =>
    setError(caught instanceof DonorApiError ? caught.message : fallback);

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-labelledby="signin-sheet-title">
      <div className="sheet__panel lattice-edge" ref={panelRef} tabIndex={-1}>
        <button type="button" className="sheet__close" onClick={onDismiss} aria-label="Close">
          &#x2715;
        </button>
        {step.kind === "choices" ? (
          <SignInChoices
            busy={busy}
            headingId="signin-sheet-title"
            heading={heading}
            lede={lede}
            appleOffered={providers.appleOffered}
            onApple={() => void providers.signInWithApple(onIssued)}
            googleOffered={providers.googleOffered}
            onGoogle={() => void providers.signInWithGoogle(onIssued)}
            footer={<InviteCodeEntry access={access} />}
            onPhone={(phone) => {
              setError(null);
              setBusy(true);
              startDonorSignIn(phone)
                .then((started) =>
                  setStep({
                    kind: "code",
                    phone,
                    verificationId: started.verificationId,
                    hint: started.developmentCode,
                  }),
                )
                .catch((caught: unknown) => fail(caught, "Could not send a code."))
                .finally(() => setBusy(false));
            }}
          />
        ) : step.kind === "invite" ? (
          <InviteRequiredStep
            attempt={step.attempt}
            refusal={step.refusal}
            access={access}
            headingId="signin-sheet-title"
            onIssued={onIssued}
            onRestart={() => setStep({ kind: "choices" })}
          />
        ) : (
          <CodeStep
            hint={step.hint}
            busy={busy}
            headingId="signin-sheet-title"
            onSubmit={(code) => {
              if (confirmed.current) return;
              confirmed.current = true;
              setError(null);
              setBusy(true);
              const attempt = {
                door: "phone" as const,
                verificationId: step.verificationId,
                code,
                phone: step.phone,
              };
              completePhoneSignIn(
                {
                  verificationId: attempt.verificationId,
                  code,
                  phone: attempt.phone,
                  ...access.fields(),
                },
                storedToken(),
              )
                .then((issued) => onIssued(issued))
                .catch((caught: unknown) => {
                  confirmed.current = false;
                  if (isSignUpRefusal(caught)) refused(attempt, caught);
                  else fail(caught, "That code is not correct.");
                })
                .finally(() => setBusy(false));
            }}
          />
        )}
        {error && (
          <p className="notice notice--error" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
